import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * Extract items array from a paginated API response.
 * Works with both new format { items: [...], total, page, ... } and legacy array format.
 */
export const unwrapItems = (data) => {
  if (Array.isArray(data)) return data;
  if (data && Array.isArray(data.items)) return data.items;
  return [];
};

/**
 * Extract pagination metadata from a paginated API response.
 */
export const getPaginationMeta = (data) => {
  if (data && data.total !== undefined) {
    return { total: data.total, page: data.page, pageSize: data.page_size, totalPages: data.total_pages, hasNext: data.has_next, hasPrev: data.has_prev };
  }
  return { total: Array.isArray(data) ? data.length : 0, page: 1, pageSize: 50, totalPages: 1, hasNext: false, hasPrev: false };
};

/**
 * Fetch paginated data from any API endpoint.
 * Returns: { items: [], total, page, total_pages }
 * Use in list components that need pagination controls.
 */
export const fetchPaged = async (url, params = {}) => {
  const response = await api.get(url, { params });
  const data = response.data;
  return {
    items: data?.items || (Array.isArray(data) ? data : []),
    total: data?.total || 0,
    page: data?.page || 1,
    total_pages: data?.total_pages || 1,
  };
};

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000, // 30s default timeout to prevent hanging requests
});

// Request interceptor to add token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('prismoline_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Handle 401 - only redirect if NOT on the login page already
    if (error.response?.status === 401 && !error.config?.url?.includes('/auth/login')) {
      localStorage.removeItem('prismoline_token');
      localStorage.removeItem('prismoline_user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// ==================== AUTH APIs ====================

export const authAPI = {
  login: async (email, password) => {
    const response = await api.post('/auth/login', { email, password });
    return response.data;
  },

  register: async (userData) => {
    const response = await api.post('/auth/register', userData);
    return response.data;
  },

  getCurrentUser: async () => {
    const response = await api.get('/auth/me');
    return response.data;
  },

  refreshToken: async (refreshToken) => {
    const response = await api.post('/auth/refresh', { refresh_token: refreshToken });
    return response.data;
  },

  listUsers: async (params = {}) => {
    const response = await api.get('/auth/users', { params: { include_inactive: true, ...params } });
    return response.data;
  },

  listSalesmen: async () => {
    const response = await api.get('/auth/salesmen');
    return response.data;
  },

  updateUser: async (userId, data) => {
    const response = await api.put(`/auth/users/${userId}`, data);
    return response.data;
  },

  toggleUserActive: async (userId) => {
    const response = await api.put(`/auth/users/${userId}/toggle-active`);
    return response.data;
  },

  resetPassword: async (userId, newPassword) => {
    const response = await api.put(`/auth/users/${userId}/reset-password`, { new_password: newPassword });
    return response.data;
  },

  deleteUser: async (userId) => {
    const response = await api.delete(`/auth/users/${userId}`);
    return response.data;
  },
};

// ==================== CUSTOMER APIs ====================

export const customerAPI = {
  getAll: async (params = {}) => {
    const response = await api.get('/sales/customers', { params });
    return unwrapItems(response.data);
  },

  getById: async (id) => {
    const response = await api.get(`/sales/customers/${id}`);
    return response.data;
  },

  create: async (customerData) => {
    const response = await api.post('/sales/customers', customerData);
    return response.data;
  },

  update: async (id, customerData) => {
    const response = await api.put(`/sales/customers/${id}`, customerData);
    return response.data;
  },

  delete: async (id) => {
    await api.delete(`/sales/customers/${id}`);
  },

  getStats: async () => {
    const response = await api.get('/sales/customers/stats/summary');
    return response.data;
  },

  getShippingAddresses: async (customerId) => {
    const response = await api.get(`/sales/customers/${customerId}/shipping-addresses`);
    return response.data;
  },

  addShippingAddress: async (customerId, address) => {
    const response = await api.post(`/sales/customers/${customerId}/shipping-addresses`, address);
    return response.data;
  },

  removeShippingAddress: async (customerId, addressId) => {
    const response = await api.delete(`/sales/customers/${customerId}/shipping-addresses/${addressId}`);
    return response.data;
  },

  creditCheck: async (customerId) => {
    const response = await api.get(`/sales/customers/${customerId}/credit-check`);
    return response.data;
  },
};

// ==================== PIPELINE APIs ====================

export const pipelineAPI = {
  getAll: async (params = {}) => {
    const response = await api.get('/sales/pipeline', { params });
    return unwrapItems(response.data);
  },

  getById: async (id) => {
    const response = await api.get(`/sales/pipeline/${id}`);
    return response.data;
  },

  create: async (leadData) => {
    const response = await api.post('/sales/pipeline', leadData);
    return response.data;
  },

  update: async (id, leadData) => {
    const response = await api.put(`/sales/pipeline/${id}`, leadData);
    return response.data;
  },

  delete: async (id) => {
    await api.delete(`/sales/pipeline/${id}`);
  },

  getStats: async (params = {}) => {
    const response = await api.get('/sales/pipeline/stats/summary', { params });
    return response.data;
  },
};

// ==================== QUOTATION APIs ====================

export const quotationAPI = {
  getAll: async (params = {}) => {
    const response = await api.get('/sales/quotations', { params });
    return unwrapItems(response.data);
  },

  getById: async (id) => {
    const response = await api.get(`/sales/quotations/${id}`);
    return response.data;
  },

  create: async (quotationData) => {
    const response = await api.post('/sales/quotations', quotationData);
    return response.data;
  },

  update: async (id, quotationData) => {
    const response = await api.put(`/sales/quotations/${id}`, quotationData);
    return response.data;
  },

  revise: async (id, quotationData) => {
    const response = await api.post(`/sales/quotations/${id}/revise`, quotationData);
    return response.data;
  },

  updateStatus: async (id, newStatus) => {
    const response = await api.patch(`/sales/quotations/${id}/status?new_status=${newStatus}`);
    return response.data;
  },

  delete: async (id) => {
    await api.delete(`/sales/quotations/${id}`);
  },

  getStats: async (params = {}) => {
    const response = await api.get('/sales/quotations/stats/summary', { params });
    return response.data;
  },
};

// ==================== BRANCH APIs ====================

export const branchAPI = {
  getAll: async (params = {}) => {
    const response = await api.get('/branches', { params });
    return unwrapItems(response.data);
  },

  getById: async (id) => {
    const response = await api.get(`/branches/${id}`);
    return response.data;
  },

  create: async (branchData) => {
    const response = await api.post('/branches', branchData);
    return response.data;
  },

  update: async (id, branchData) => {
    const response = await api.put(`/branches/${id}`, branchData);
    return response.data;
  },

  delete: async (id) => {
    await api.delete(`/branches/${id}`);
  },

  getStats: async () => {
    const response = await api.get('/branches/stats/summary');
    return response.data;
  },

  getBillingAddress: async (branchId) => {
    const response = await api.get(`/purchase/branch-address/${branchId}`);
    return response.data;
  },
};

// ==================== PURCHASE APIs ====================

export const purchaseAPI = {
  getAll: async (params = {}) => {
    const response = await api.get('/purchase', { params });
    return unwrapItems(response.data);
  },

  getStatusReport: async (params = {}) => {
    const response = await api.get('/purchase/reports/status', { params });
    return response.data;
  },

  getById: async (id) => {
    const response = await api.get(`/purchase/${id}`);
    return response.data;
  },

  create: async (poData) => {
    const response = await api.post('/purchase', poData);
    return response.data;
  },

  update: async (id, poData) => {
    const response = await api.put(`/purchase/${id}`, poData);
    return response.data;
  },

  updateStatus: async (id, newStatus) => {
    const response = await api.patch(`/purchase/${id}/status?new_status=${newStatus}`);
    return response.data;
  },

  getStats: async (params = {}) => {
    const response = await api.get('/purchase/stats/summary', { params });
    return response.data;
  },

  addUpdate: async (poId, text) => {
    const response = await api.post(`/purchase/${poId}/updates`, { text });
    return response.data;
  },

  getUpdates: async (poId) => {
    const response = await api.get(`/purchase/${poId}/updates`);
    return response.data;
  },

  cancel: async (id) => {
    const response = await api.put(`/purchase/${id}/cancel`);
    return response.data;
  },

  confirmDraft: async (id) => {
    const response = await api.put(`/purchase/${id}/confirm`);
    return response.data;
  },
};

// ==================== INDENT APIs ====================

export const indentAPI = {
  getAll: async (params = {}) => {
    const response = await api.get('/indents', { params });
    return unwrapItems(response.data);
  },

  getById: async (id) => {
    const response = await api.get(`/indents/${id}`);
    return response.data;
  },

  create: async (indentData) => {
    const response = await api.post('/indents', indentData);
    return response.data;
  },

  update: async (id, updateData) => {
    const response = await api.put(`/indents/${id}`, updateData);
    return response.data;
  },

  linkPO: async (indentId, poId) => {
    const response = await api.patch(`/indents/${indentId}/link-po?po_id=${poId}`);
    return response.data;
  },

  convertToPO: async (indentId, poData) => {
    const response = await api.post(`/indents/${indentId}/convert-to-po`, poData);
    return response.data;
  },

  getStats: async (params = {}) => {
    const response = await api.get('/indents/stats/summary', { params });
    return response.data;
  },

  cancel: async (id) => {
    const response = await api.put(`/indents/${id}/cancel`);
    return response.data;
  },
};

// ==================== STOCK APIs ====================

export const stockAPI = {
  getAll: async (params = {}) => {
    const response = await api.get('/stock', { params });
    return unwrapItems(response.data);
  },

  getById: async (id) => {
    const response = await api.get(`/stock/${id}`);
    return response.data;
  },

  create: async (stockData) => {
    const response = await api.post('/stock', stockData);
    return response.data;
  },

  update: async (id, stockData) => {
    const response = await api.put(`/stock/${id}`, stockData);
    return response.data;
  },

  getStats: async (params = {}) => {
    const response = await api.get('/stock/stats/summary', { params });
    return response.data;
  },

  getLinkedSummary: async () => {
    const response = await api.get('/stock/linked-summary');
    return response.data;
  },

  // Linked Items API
  linkItems: async (data) => {
    const response = await api.post('/stock/linked-items', data);
    return response.data;
  },

  getLinkedItems: async () => {
    const response = await api.get('/stock/linked-items');
    return response.data;
  },

  unlinkItems: async (linkId) => {
    const response = await api.delete(`/stock/linked-items/${linkId}`);
    return response.data;
  },

  getLinkedForProduct: async (productId) => {
    const response = await api.get(`/stock/linked-items/for-product/${productId}`);
    return response.data;
  },

  getConsolidated: async (params = {}) => {
    const response = await api.get('/stock/consolidated/by-product', { params });
    return response.data;
  },

  updateProductFields: async (data) => {
    const response = await api.put('/stock/consolidated/update-product', data);
    return response.data;
  },

  adjustQuantity: async (data) => {
    const response = await api.put('/stock/consolidated/adjust-quantity', data);
    return response.data;
  },

  getManualAdjustments: async (params) => {
    const response = await api.get('/stock/manual-adjustments', { params });
    return response.data;
  },

  setOpeningStock: async (data) => {
    const response = await api.put('/stock/opening-stock/set', data);
    return response.data;
  },

  revertAdjustment: async (adjustmentId, reason) => {
    const response = await api.post(`/stock/adjustments/${adjustmentId}/revert`, { reason });
    return response.data;
  },

  getManualAdjustmentsCount: async () => {
    const response = await api.get('/stock/manual-adjustments/count');
    return response.data;
  },

  deleteAllStockWithAdjustments: async () => {
    const response = await api.delete('/stock/delete-all-stock-with-adjustments');
    return response.data;
  },

  importOpeningStock: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post('/stock/import-opening', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120000,
    });
    return response.data;
  },

  replaceOpeningStock: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post('/stock/replace-opening-stock', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120000,
    });
    return response.data;
  },

  bulkSetOpeningStock: async (file, revertExistingAdjustments = true) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post('/stock/opening-stock/bulk-set', formData, {
      params: { revert_existing_adjustments: revertExistingAdjustments },
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 180000,
    });
    return response.data;
  },

  healOpeningSetDrift: async () => {
    const response = await api.post('/stock/heal-opening-set-drift');
    return response.data;
  },

  downloadOpeningStockTemplate: async () => {
    const response = await api.get('/stock/opening-stock-template', { responseType: 'blob' });
    return response.data;
  },

  exportAsOnDate: async (asOnDate, branch) => {
    const params = { as_on_date: asOnDate };
    if (branch) params.branch = branch;
    const response = await api.get('/stock/export/as-on-date', { params, responseType: 'blob' });
    return response.data;
  },

  exportCurrentStock: async (branch) => {
    const params = {};
    if (branch) params.branch = branch;
    const response = await api.get('/stock/export/current', { params, responseType: 'blob' });
    return response.data;
  },

  batchNetQty: async (branchId, productIds) => {
    const response = await api.post('/stock/batch-net-qty', { branch_id: branchId, product_ids: productIds });
    // Returns { stock: {pid: available}, details: {pid: {physical_balance, reserved_awaiting_invoice, available}} }
    return { stock: response.data.stock || {}, details: response.data.details || {} };
  },

  stockSanity: async (params = {}) => {
    const response = await api.get('/reports/stock-sanity', { params });
    return response.data;
  },
};

// ==================== PRODUCT APIs ====================

export const productAPI = {
  getAll: async (params = {}) => {
    const response = await api.get('/products', { params });
    return unwrapItems(response.data);
  },
  repairStockReferences: async () => (await api.post('/products/repair-stock-references')).data,

  getById: async (id) => {
    const response = await api.get(`/products/${id}`);
    return response.data;
  },

  create: async (productData) => {
    const response = await api.post('/products', productData);
    return response.data;
  },

  update: async (id, productData) => {
    const response = await api.put(`/products/${id}`, productData);
    return response.data;
  },

  delete: async (id) => {
    await api.delete(`/products/${id}`);
  },

  getHistory: async (id, branchId) => {
    const params = {};
    if (branchId) params.branch = branchId;
    const response = await api.get(`/products/${id}/history`, { params });
    return response.data;
  },

  bulkImportPreview: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post('/products/bulk-import/preview', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  bulkImportConfirm: async (data) => {
    const response = await api.post('/products/bulk-import/confirm', data);
    return response.data;
  },

  hsnLookup: async (descriptions) => {
    const response = await api.post('/products/hsn-lookup', { descriptions });
    return response.data;
  },

  listSubCategories: async () => {
    const response = await api.get('/products/sub-categories/list');
    return response.data;
  },

  bulkUpdateSubCategory: async (productIds, subCategory) => {
    const response = await api.post('/products/bulk-update-sub-category', {
      product_ids: productIds,
      sub_category: subCategory,
    });
    return response.data;
  },
};

// ==================== SUB-CATEGORY SALES REPORT ====================
export const subCategorySalesReportAPI = {
  getBreakdown: async (params) => {
    const response = await api.get('/reports/sales/sub-category-breakdown', { params });
    return response.data;
  },
};

// ==================== VENDOR APIs ====================

export const vendorAPI = {
  getAll: async (params = {}) => {
    const response = await api.get('/vendors', { params });
    return unwrapItems(response.data);
  },

  getById: async (id) => {
    const response = await api.get(`/vendors/${id}`);
    return response.data;
  },

  create: async (vendorData) => {
    const response = await api.post('/vendors', vendorData);
    return response.data;
  },

  update: async (id, vendorData) => {
    const response = await api.put(`/vendors/${id}`, vendorData);
    return response.data;
  },

  delete: async (id) => {
    await api.delete(`/vendors/${id}`);
  },

  getAnalytics: async () => {
    const response = await api.get('/vendors/analytics/performance');
    return response.data;
  },

  getShippingAddresses: async (vendorId) => {
    const response = await api.get(`/vendors/${vendorId}/shipping-addresses`);
    return response.data;
  },

  addShippingAddress: async (vendorId, address) => {
    const response = await api.post(`/vendors/${vendorId}/shipping-addresses`, address);
    return response.data;
  },

  removeShippingAddress: async (vendorId, addressId) => {
    const response = await api.delete(`/vendors/${vendorId}/shipping-addresses/${addressId}`);
    return response.data;
  },
};

// ==================== NOTIFICATION APIs ====================

export const notificationAPI = {
  getAll: async (unreadOnly = false) => {
    const response = await api.get('/notifications', { params: { unread_only: unreadOnly } });
    return unwrapItems(response.data);
  },

  markAsRead: async (id) => {
    const response = await api.patch(`/notifications/${id}/read`);
    return response.data;
  },

  checkOverduePOs: async () => {
    const response = await api.post('/notifications/check-overdue-pos');
    return response.data;
  },

  clearAll: async () => {
    const response = await api.delete('/notifications/clear-all');
    return response.data;
  },
};

// ==================== PRODUCTION APIs ====================

export const productionAPI = {
  getAll: async (params = {}) => {
    const response = await api.get('/production', { params });
    return unwrapItems(response.data);
  },

  getById: async (id) => {
    const response = await api.get(`/production/${id}`);
    return response.data;
  },

  create: async (data) => {
    const response = await api.post('/production', data);
    return response.data;
  },

  update: async (id, data) => {
    const response = await api.put(`/production/${id}`, data);
    return response.data;
  },

  getStats: async (params = {}) => {
    const response = await api.get('/production/stats/summary', { params });
    return response.data;
  },

  getBreakdown: async (params = {}) => {
    const response = await api.get('/production/reports/breakdown', { params });
    return response.data;
  },

  getFromOC: async (ocId) => {
    const response = await api.get(`/production/from-oc/${ocId}`);
    return response.data;
  },

  autoFromShortfall: async (data) => {
    const response = await api.post('/production/auto-from-shortfall', data);
    return response.data;
  },

  updateMaterials: async (id, materials) => {
    const response = await api.put(`/production/${id}/materials`, { materials });
    return response.data;
  },

  updateCosts: async (id, materials) => {
    const response = await api.put(`/production/${id}/update-costs`, { materials });
    return response.data;
  },
};

// ==================== FORECAST APIs ====================

export const forecastAPI = {
  getAIAnalysis: async (query, context = 'general', sessionId = null) => {
    const payload = { query, context };
    if (sessionId) payload.session_id = sessionId;
    const response = await api.post('/forecast/ai-analysis', payload, { timeout: 120000 });
    return response.data;
  },

  convertUnits: async (data) => {
    const response = await api.post('/forecast/convert-units', data);
    return response.data;
  },

  getERPSummary: async () => {
    const response = await api.get('/forecast/erp-summary');
    return response.data;
  },

  materialPlanning: async (targets, branchId = null) => {
    const response = await api.post('/forecast/material-planning', { targets, branch_id: branchId }, { timeout: 60000 });
    return response.data;
  },
};

// ==================== DISPATCH APIs ====================


export const inwardAPI = {
  getAll: async (params = {}) => {
    const response = await api.get('/inwards', { params });
    return unwrapItems(response.data);
  },
  getById: async (id) => {
    const response = await api.get(`/inwards/${id}`);
    return response.data;
  },
  create: async (data) => {
    const response = await api.post('/inwards', data);
    return response.data;
  },
  update: async (id, data) => {
    const response = await api.put(`/inwards/${id}`, data);
    return response.data;
  },
  getStats: async () => {
    const response = await api.get('/inwards/stats/summary');
    return response.data;
  },
  poLookup: async (poId) => {
    const response = await api.get(`/inwards/po-lookup/${poId}`);
    return response.data;
  },
  getFreightAnalytics: async (params = {}) => {
    const response = await api.get('/inwards/freight/analytics', { params });
    return response.data;
  },
  getLandedCostReport: async (params = {}) => {
    const response = await api.get('/inwards/reports/landed-cost', { params });
    return response.data;
  },
  updatePurchaseInvoice: async (id, data) => {
    const response = await api.put(`/inwards/${id}/purchase-invoice`, data);
    return response.data;
  },

  cancel: async (id) => {
    const response = await api.put(`/inwards/${id}/cancel`);
    return response.data;
  },
};

export const dispatchAPI = {
  getAll: async (params = {}) => {
    const response = await api.get('/dispatches', { params });
    return unwrapItems(response.data);
  },

  getById: async (id) => {
    const response = await api.get(`/dispatches/${id}`);
    return response.data;
  },

  create: async (data) => {
    const response = await api.post('/dispatches', data);
    return response.data;
  },

  update: async (id, data) => {
    const response = await api.put(`/dispatches/${id}`, data);
    return response.data;
  },

  updateVehicleNumber: async (id, vehicleNumber) => {
    const response = await api.put(`/dispatches/${id}/vehicle-number`, { vehicle_number: vehicleNumber });
    return response.data;
  },

  cancel: async (id, reason) => {
    const response = await api.post(`/dispatches/${id}/cancel`, { reason });
    return response.data;
  },

  getStats: async (params = {}) => {
    const response = await api.get('/dispatches/stats/summary', { params });
    return response.data;
  },

  getFromOC: async (ocId) => {
    const response = await api.get(`/dispatches/from-oc/${ocId}`);
    return response.data;
  },

  searchOC: async (q) => {
    const response = await api.get('/dispatches/search-oc', { params: { q } });
    return response.data;
  },
  updateExtraCharges: async (id, charges) => {
    const response = await api.put(`/dispatches/${id}/extra-charges`, { extra_charges: charges });
    return response.data;
  },
  getAllExtraCharges: async (params = {}) => {
    const response = await api.get('/dispatches/extra-charges/all', { params });
    return response.data;
  },
  getTransporterReport: async () => {
    const response = await api.get('/dispatches/extra-charges/transporter-report');
    return response.data;
  },

  getExtraChargesDispatchSummary: async (params = {}) => {
    const response = await api.get('/dispatches/extra-charges/dispatch-summary', { params });
    return response.data;
  },

  markExtraChargesPaid: async (dispatchId, data) => {
    const response = await api.put(`/dispatches/${dispatchId}/extra-charges-payment`, data);
    return response.data;
  },

  // Freight Tracker
  getFreightAnalytics: async (params = {}) => {
    const response = await api.get('/dispatches/freight/analytics', { params });
    return response.data;
  },

  getFreightDispatches: async (params = {}) => {
    const response = await api.get('/dispatches/freight/dispatches', { params });
    return response.data;
  },

  markFreightPaid: async (dispatchId, data) => {
    const response = await api.post(`/dispatches/${dispatchId}/mark-freight-paid`, data);
    return response.data;
  },
};

// ==================== STOCK TRANSFER APIs ====================

export const stockTransferAPI = {
  getAll: async (params = {}) => {
    const response = await api.get('/stock-transfers', { params });
    return response.data;
  },

  getById: async (id) => {
    const response = await api.get(`/stock-transfers/${id}`);
    return response.data;
  },

  create: async (data) => {
    const response = await api.post('/stock-transfers', data);
    return response.data;
  },

  createSignageProduct: async (spec) => {
    const response = await api.post('/stock-transfers/signage-product', spec);
    return response.data;
  },

  update: async (id, data) => {
    const response = await api.put(`/stock-transfers/${id}`, data);
    return response.data;
  },

  delete: async (id) => {
    await api.delete(`/stock-transfers/${id}`);
  },

  getStats: async () => {
    const response = await api.get('/stock-transfers/stats/summary');
    return response.data;
  },

  confirm: async (stnId) => {
    const response = await api.post(`/stock-transfers/${stnId}/confirm`);
    return response.data;
  },

  readyToDispatch: async (stnId, data) => {
    const response = await api.post(`/stock-transfers/${stnId}/ready-to-dispatch`, data);
    return response.data;
  },

  stockCheck: async (stnId) => {
    const response = await api.post(`/stock-transfers/${stnId}/stock-check`);
    return response.data;
  },

  uploadInvoice: async (stnId, data) => {
    const response = await api.post(`/stock-transfers/${stnId}/upload-invoice`, data);
    return response.data;
  },

  confirmDispatch: async (stnId) => {
    const response = await api.post(`/stock-transfers/${stnId}/confirm-dispatch`);
    return response.data;
  },

  receive: async (stnId) => {
    const response = await api.post(`/stock-transfers/${stnId}/receive`);
    return response.data;
  },

  cancel: async (stnId, reason) => {
    const response = await api.post(`/stock-transfers/${stnId}/cancel`, { reason });
    return response.data;
  },

  amend: async (stnId, data) => {
    const response = await api.put(`/stock-transfers/${stnId}/amend`, data);
    return response.data;
  },

  updateVehicleNumber: async (stnId, vehicleNumber) => {
    const response = await api.put(`/stock-transfers/${stnId}/vehicle-number`, { vehicle_number: vehicleNumber });
    return response.data;
  },
};

// ===== FILE API =====
export const fileAPI = {
  upload: async (file, entityType, entityId) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post(`/files/upload?entity_type=${entityType}&entity_id=${entityId}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  uploadDocument: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post('/files/upload-document', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  download: async (path) => {
    const response = await api.get(`/files/download?path=${encodeURIComponent(path)}`, {
      responseType: 'blob',
    });
    return response.data;
  },

  getDownloadUrl: (path) => {
    const base = api.defaults.baseURL || '';
    return `${base}/files/download?path=${encodeURIComponent(path)}`;
  },
};

// ==================== SIGNAGE APIs ====================

export const signageAPI = {
  getRateCard: async () => {
    const response = await api.get('/signage/rate-card');
    return response.data;
  },

  updateRateCard: async (cardData) => {
    const response = await api.put('/signage/rate-card', cardData);
    return response.data;
  },

  calculate: async (calcData) => {
    const response = await api.post('/signage/calculate', calcData);
    return response.data;
  },

  getLaminates: async () => {
    const response = await api.get('/signage/laminates');
    return response.data;
  },
};

export default api;

// ==================== APPROVAL APIs ====================

export const approvalAPI = {
  getAll: async (status) => {
    const params = status ? { status_filter: status } : {};
    const response = await api.get('/customer-approvals', { params });
    return unwrapItems(response.data);
  },

  getById: async (id) => {
    const response = await api.get(`/customer-approvals/${id}`);
    return response.data;
  },

  create: async (data) => {
    const response = await api.post('/customer-approvals', data);
    return response.data;
  },

  approve: async (id, data = {}) => {
    const response = await api.put(`/customer-approvals/${id}/approve`, data);
    return response.data;
  },

  reject: async (id, data) => {
    const response = await api.put(`/customer-approvals/${id}/reject`, data);
    return response.data;
  },

  getStats: async () => {
    const response = await api.get('/customer-approvals/stats/summary');
    return response.data;
  },
};


// ==================== ORDER CONFIRMATION APIs ====================

export const orderConfirmationAPI = {
  getAll: async (params = {}) => {
    const response = await api.get('/order-confirmations', { params });
    return unwrapItems(response.data);
  },

  getById: async (id) => {
    const response = await api.get(`/order-confirmations/${id}`);
    return response.data;
  },

  create: async (data) => {
    const response = await api.post('/order-confirmations', data);
    return response.data;
  },

  createFromQuotation: async (quoteId) => {
    const response = await api.post(`/order-confirmations/from-quotation/${quoteId}`);
    return response.data;
  },

  updateStatus: async (id, status) => {
    const response = await api.put(`/order-confirmations/${id}/status`, { status });
    return response.data;
  },

  markReadyToDispatch: async (id, data) => {
    const response = await api.put(`/order-confirmations/${id}/ready-to-dispatch`, data);
    return response.data;
  },

  updateInvoice: async (id, data) => {
    const response = await api.put(`/order-confirmations/${id}/invoice`, data);
    return response.data;
  },

  updateVehicleNumber: async (id, vehicleNumber) => {
    const response = await api.put(`/order-confirmations/${id}/vehicle-number`, { vehicle_number: vehicleNumber });
    return response.data;
  },

  bulkUpdateStatus: async (ocIds, status) => {
    const response = await api.put('/order-confirmations/bulk-status', { oc_ids: ocIds, status });
    return response.data;
  },

  stockCheck: async (data) => {
    const response = await api.post('/order-confirmations/stock-check', data);
    return response.data;
  },

  confirmDraft: async (id, notes = '') => {
    const response = await api.put(`/order-confirmations/${id}/confirm`, { notes });
    return response.data;
  },

  editDraft: async (id, data) => {
    const response = await api.put(`/order-confirmations/${id}/edit-draft`, data);
    return response.data;
  },

  confirmDispatch: async (id) => {
    const response = await api.put(`/order-confirmations/${id}/confirm-dispatch`);
    return response.data;
  },

  reconcileDispatch: async (id, payload = {}) => {
    const response = await api.post(`/order-confirmations/${id}/reconcile-dispatch`, payload);
    return response.data;
  },

  productBindingReport: async (onlyActive = true) => {
    const response = await api.get(`/order-confirmations/admin/oc-product-binding-report`, { params: { only_active: onlyActive } });
    return response.data;
  },

  productBindingHeal: async (payload = { dry_run: true }) => {
    const response = await api.post(`/order-confirmations/admin/oc-product-binding-heal`, payload);
    return response.data;
  },

  swapBranch: async (id, payload) => {
    const response = await api.post(`/order-confirmations/admin/${id}/swap-branch`, payload);
    return response.data;
  },

  createStnDraft: async (id) => {
    const response = await api.post(`/order-confirmations/${id}/create-stn-draft`);
    return response.data;
  },

  getLinkedStns: async (id) => {
    const response = await api.get(`/order-confirmations/${id}/linked-stns`);
    return response.data;
  },
};


// ==================== OC Amendments APIs ====================
export const ocAmendmentAPI = {
  submit: async (ocId, data) => {
    const response = await api.post(`/oc-amendments/${ocId}`, data);
    return response.data;
  },
  list: async (params = {}) => {
    const response = await api.get('/oc-amendments', { params });
    return response.data;
  },
  stats: async () => {
    const response = await api.get('/oc-amendments/stats');
    return response.data;
  },
  getForOC: async (ocId) => {
    const response = await api.get(`/oc-amendments/oc/${ocId}`);
    return response.data;
  },
  getById: async (id) => {
    const response = await api.get(`/oc-amendments/${id}`);
    return response.data;
  },
  approve: async (id, notes = '') => {
    const response = await api.put(`/oc-amendments/${id}/approve`, { notes });
    return response.data;
  },
  reject: async (id, notes) => {
    const response = await api.put(`/oc-amendments/${id}/reject`, { notes });
    return response.data;
  },
  report: async (params = {}) => {
    const response = await api.get('/reports/amended-ocs', { params });
    return response.data;
  },
};


// ==================== BOM APIs ====================

// ==================== PLANNING APIs ====================

export const planningAPI = {
  // Forecasts
  createForecast: async (data) => {
    const response = await api.post('/planning/forecasts', data);
    return response.data;
  },
  listForecasts: async (params = {}) => {
    const response = await api.get('/planning/forecasts', { params });
    return response.data;
  },
  updateForecast: async (id, data) => {
    const response = await api.put(`/planning/forecasts/${id}`, data);
    return response.data;
  },
  deleteForecast: async (id) => {
    const response = await api.delete(`/planning/forecasts/${id}`);
    return response.data;
  },

  // Production Plan
  getProductionPlan: async (view = 'monthly', month = '') => {
    const params = { view };
    if (month) params.month = month;
    const response = await api.get('/planning/production-plan', { params });
    return response.data;
  },

  // OC Shortfall
  getOCShortfall: async () => {
    const response = await api.get('/planning/oc-shortfall');
    return response.data;
  },

  // Material Requirements (MRP)
  getMaterialRequirements: async (month = '') => {
    const params = month ? { month } : {};
    const response = await api.get('/planning/material-requirements', { params });
    return response.data;
  },

  // Indent Summary
  getIndentSummary: async (month = '') => {
    const params = month ? { month } : {};
    const response = await api.get('/planning/indent-summary', { params });
    return response.data;
  },

  // AI Suggestions
  getAISuggestions: async (month) => {
    const response = await api.post('/planning/ai-suggest', { month });
    return response.data;
  },

  // Generate Indent from MRP
  generateIndent: async (data) => {
    const response = await api.post('/planning/generate-indent', data);
    return response.data;
  },
};

// ==================== BILLING ADDRESS APIs ====================

export const billingAddressAPI = {
  createRequest: async (data) => {
    const response = await api.post('/billing-addresses/request', data);
    return response.data;
  },
  listRequests: async (params = {}) => {
    const response = await api.get('/billing-addresses/requests', { params });
    return response.data;
  },
  getApprovedList: async () => {
    const response = await api.get('/billing-addresses/approved-list');
    return response.data;
  },
  approve: async (id, data) => {
    const response = await api.put(`/billing-addresses/requests/${id}/approve`, data);
    return response.data;
  },
  reject: async (id, data) => {
    const response = await api.put(`/billing-addresses/requests/${id}/reject`, data);
    return response.data;
  },
};


export const bomAPI = {
  getAll: async (params = {}) => {
    const response = await api.get('/boms', { params });
    return unwrapItems(response.data);
  },

  getAllPaginated: async (params = {}) => {
    const response = await api.get('/boms', { params });
    const list = Array.isArray(response.data) ? response.data : (response.data?.items || []);
    const total = response.data && response.data.total !== undefined ? response.data.total : list.length;
    return { items: list, total };
  },

  getById: async (id) => {
    const response = await api.get(`/boms/${id}`);
    return response.data;
  },

  create: async (data) => {
    const response = await api.post('/boms', data);
    return response.data;
  },

  update: async (id, data) => {
    const response = await api.put(`/boms/${id}`, data);
    return response.data;
  },

  getStats: async () => {
    const response = await api.get('/boms/stats/summary');
    return response.data;
  },

  getNextNumber: async () => {
    const response = await api.get('/boms/next-number');
    return response.data;
  },

  downloadTemplate: () => {
    const url = `${API_BASE_URL}/boms/import/template`;
    const a = document.createElement('a');
    a.href = url;
    a.download = 'BOM_Import_Template.xlsx';
    const token = localStorage.getItem('prismoline_token');
    if (token) {
      // Use fetch to include auth header
      return fetch(url, { headers: { Authorization: `Bearer ${token}` } })
        .then(res => res.blob())
        .then(blob => {
          const blobUrl = URL.createObjectURL(blob);
          a.href = blobUrl;
          a.click();
          URL.revokeObjectURL(blobUrl);
        });
    }
    a.click();
  },

  exportExcel: (params = {}) => {
    const qs = new URLSearchParams(
      Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '')
    ).toString();
    const url = `${API_BASE_URL}/boms/export${qs ? `?${qs}` : ''}`;
    const token = localStorage.getItem('prismoline_token');
    return fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} })
      .then(res => {
        if (!res.ok) throw new Error('Export failed');
        return res.blob();
      })
      .then(blob => {
        const blobUrl = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = `BOMs_Export_${new Date().toISOString().slice(0, 10)}.xlsx`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(blobUrl);
      });
  },

  importPreview: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post('/boms/import/preview', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  importConfirm: async (data) => {
    const response = await api.post('/boms/import/confirm', data);
    return response.data;
  },

  simulate: async (bomId, overrides) => {
    const response = await api.post(`/boms/${bomId}/simulate`, overrides);
    return response.data;
  },

  checkAvailability: async (bomId, qty = 1) => {
    const response = await api.get(`/boms/${bomId}/availability`, { params: { qty } });
    return response.data;
  },

  getFIFORates: async (bomId) => {
    const response = await api.get(`/boms/${bomId}/fifo-rates`);
    return response.data;
  },

  // Approvals
  submitApproval: async (data) => {
    const response = await api.post('/boms/approvals', data);
    return response.data;
  },

  listApprovals: async (status) => {
    const params = status ? { status_filter: status } : {};
    const response = await api.get('/boms/approvals/list', { params });
    return response.data;
  },

  approveRequest: async (id, data = {}) => {
    const response = await api.put(`/boms/approvals/${id}/approve`, data);
    return response.data;
  },

  rejectRequest: async (id, data) => {
    const response = await api.put(`/boms/approvals/${id}/reject`, data);
    return response.data;
  },
};



export const savedAddressAPI = {
  getAll: async (params = {}) => {
    const response = await api.get('/saved-addresses', { params });
    return unwrapItems(response.data);
  },
  getByType: async (type) => {
    const response = await api.get('/saved-addresses', { params: { address_type: type } });
    return response.data;
  },
  create: async (data) => {
    const response = await api.post('/saved-addresses', data);
    return response.data;
  },
  update: async (id, data) => {
    const response = await api.put(`/saved-addresses/${id}`, data);
    return response.data;
  },
  delete: async (id) => {
    const response = await api.delete(`/saved-addresses/${id}`);
    return response.data;
  },
};


export const debtorsReportAPI = {
  getReport: async () => {
    const response = await api.get('/reports/debtors');
    return response.data;
  },
};


export const searchAPI = {
  search: async (query) => {
    const response = await api.get('/search', { params: { q: query } });
    return response.data;
  },
};


export const approvalWorkflowAPI = {
  // Configs
  getConfigs: async (params = {}) => {
    const response = await api.get('/approval-workflows/configs', { params });
    return response.data;
  },
  createConfig: async (data) => {
    const response = await api.post('/approval-workflows/configs', data);
    return response.data;
  },
  updateConfig: async (id, data) => {
    const response = await api.put(`/approval-workflows/configs/${id}`, data);
    return response.data;
  },
  deleteConfig: async (id) => {
    const response = await api.delete(`/approval-workflows/configs/${id}`);
    return response.data;
  },
  // Requests
  submitForApproval: async (data) => {
    const response = await api.post('/approval-workflows/submit', data);
    return response.data;
  },
  takeAction: async (requestId, data) => {
    const response = await api.post(`/approval-workflows/requests/${requestId}/action`, data);
    return response.data;
  },
  getRequests: async (params = {}) => {
    const response = await api.get('/approval-workflows/requests', { params });
    return response.data;
  },
  getByDocument: async (documentId) => {
    const response = await api.get(`/approval-workflows/requests/by-document/${documentId}`);
    return response.data;
  },
  checkStatus: async (documentType, documentId) => {
    const response = await api.get(`/approval-workflows/check/${documentType}/${documentId}`);
    return response.data;
  },
};

export const documentTrailAPI = {
  getTrail: async (docType, docId) => {
    const response = await api.get(`/document-trail/${docType}/${docId}`);
    return response.data;
  },
  getSalesTrail: async (docType, docId) => {
    const response = await api.get(`/document-trail/sales/${docType}/${docId}`);
    return response.data;
  },
};

export const dashboardAPI = {
  getTracking: async (params = {}) => {
    const response = await api.get('/dashboard/tracking', { params });
    return response.data;
  },
};

// ==================== DOCUMENT REVISION APIs ====================

export const documentRevisionAPI = {
  getForDocument: async (docType, docId) => {
    const response = await api.get(`/document-revisions/${docType}/${docId}`);
    return response.data;
  },
  getSnapshot: async (revisionId) => {
    const response = await api.get(`/document-revisions/snapshot/${revisionId}`);
    return response.data;
  },
  getRecent: async (limit = 20, docType = null) => {
    const params = { limit };
    if (docType) params.doc_type = docType;
    const response = await api.get('/document-revisions/recent', { params });
    return response.data;
  },
};

export const salesAnalyticsAPI = {
  get: async (params = {}) => {
    const response = await api.get('/sales/analytics', { params });
    return response.data;
  },
};

export const activityAPI = {
  create: async (data) => {
    const response = await api.post('/activities', data);
    return response.data;
  },
  getForEntity: async (entityType, entityId, page = 1) => {
    const response = await api.get('/activities', { params: { entity_type: entityType, entity_id: entityId, page } });
    return response.data;
  },
  getUpcomingFollowups: async (days = 7) => {
    const response = await api.get('/activities/upcoming-followups', { params: { days } });
    return response.data;
  },
  delete: async (id) => {
    const response = await api.delete(`/activities/${id}`);
    return response.data;
  },
};

export const proformaInvoiceAPI = {
  getAll: async (params = {}) => {
    const response = await api.get('/proforma-invoices', { params });
    return unwrapItems(response.data);
  },
  getById: async (id) => {
    const response = await api.get(`/proforma-invoices/${id}`);
    return response.data;
  },
  create: async (data) => {
    const response = await api.post('/proforma-invoices', data);
    return response.data;
  },
  createFromQuotation: async (data) => {
    const response = await api.post('/proforma-invoices/from-quotation', data);
    return response.data;
  },
  updateStatus: async (id, status) => {
    const response = await api.put(`/proforma-invoices/${id}/status`, { status });
    return response.data;
  },
};

export const quotationTemplateAPI = {
  getAll: async () => {
    const response = await api.get('/quotation-templates');
    return unwrapItems(response.data);
  },
  getById: async (id) => {
    const response = await api.get(`/quotation-templates/${id}`);
    return response.data;
  },
  create: async (data) => {
    const response = await api.post('/quotation-templates', data);
    return response.data;
  },
  update: async (id, data) => {
    const response = await api.put(`/quotation-templates/${id}`, data);
    return response.data;
  },
  delete: async (id) => {
    const response = await api.delete(`/quotation-templates/${id}`);
    return response.data;
  },
};

export const salesTargetAPI = {
  getAll: async (params = {}) => {
    const response = await api.get('/sales-targets', { params });
    return unwrapItems(response.data);
  },
  create: async (data) => {
    const response = await api.post('/sales-targets', data);
    return response.data;
  },
  delete: async (id) => {
    const response = await api.delete(`/sales-targets/${id}`);
    return response.data;
  },
  getAchievement: async (params = {}) => {
    const response = await api.get('/sales-targets/achievement', { params });
    return response.data;
  },
};

export const paymentAPI = {
  getAll: async (params = {}) => {
    const response = await api.get('/payments', { params });
    return unwrapItems(response.data);
  },
  create: async (data) => {
    const response = await api.post('/payments', data);
    return response.data;
  },
  delete: async (id) => {
    const response = await api.delete(`/payments/${id}`);
    return response.data;
  },
  getCustomerLedger: async (customerId) => {
    const response = await api.get(`/payments/customer-ledger/${customerId}`);
    return response.data;
  },
};

export const paymentAgeingAPI = {
  getReport: async (asOnDate) => {
    const params = asOnDate ? { as_on_date: asOnDate } : {};
    const response = await api.get('/reports/payment-ageing', { params });
    return response.data;
  },
};

export const stockAgeingAPI = {
  getReport: async () => {
    const response = await api.get('/reports/stock-ageing');
    return response.data;
  },
};

export const stockReorderAPI = {
  getAlerts: async () => {
    const response = await api.get('/reports/stock-reorder');
    return response.data;
  },
};

export const stockRegistersAPI = {
  getGodownWise: async (params = {}) => {
    const response = await api.get('/stock/reports/godown-wise', { params });
    return response.data;
  },
  getAsOnDate: async (params = {}) => {
    const response = await api.get('/stock/reports/as-on-date', { params });
    return response.data;
  },
  getInwardOutward: async (params = {}) => {
    const response = await api.get('/stock/reports/inward-outward', { params });
    return response.data;
  },
};

export const salesReportsAPI = {
  getBranchProductSales: async (params = {}) => {
    const response = await api.get('/reports/branch-product-sales', { params });
    return response.data;
  },
  getPartyProductSales: async (params = {}) => {
    const response = await api.get('/reports/party-product-sales', { params });
    return response.data;
  },
  exportBranchProductSales: async (params = {}) => {
    const response = await api.get('/reports/branch-product-sales/export', {
      params,
      responseType: 'blob',
    });
    return response.data;
  },
  exportPartyProductSales: async (params = {}) => {
    const response = await api.get('/reports/party-product-sales/export', {
      params,
      responseType: 'blob',
    });
    return response.data;
  },
};

export const customReportsAPI = {
  getDimensions: async () => {
    const response = await api.get('/reports/custom/dimensions');
    return response.data;
  },
  getMeasures: async () => {
    const response = await api.get('/reports/custom/measures');
    return response.data;
  },
  generate: async (config) => {
    const response = await api.post('/reports/custom/generate', config);
    return response.data;
  },
  getSavedReports: async () => {
    const response = await api.get('/reports/custom/saved');
    return response.data;
  },
  saveReport: async (config) => {
    const response = await api.post('/reports/custom/save', config);
    return response.data;
  },
  deleteReport: async (id) => {
    const response = await api.delete(`/reports/custom/saved/${id}`);
    return response.data;
  },
};

export const gstinAPI = {
  lookup: async (gstin) => {
    const response = await api.get(`/gstin/lookup/${gstin}`);
    return response.data;
  },
};

export const bulkImportAPI = {
  previewContacts: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post('/bulk-import/contacts/preview', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },
  confirmContacts: async (data) => {
    const response = await api.post('/bulk-import/contacts/confirm', data);
    return response.data;
  },
  deleteAllContacts: async (target = 'all') => {
    const response = await api.delete(`/bulk-import/contacts/delete-all?target=${target}`);
    return response.data;
  },
  deleteAllProducts: async () => {
    const response = await api.delete('/products/bulk-delete-all');
    return response.data;
  },
};

export const siteProjectAPI = {
  getAll: async (params = {}) => {
    const response = await api.get('/site-projects', { params });
    return unwrapItems(response.data);
  },
  getById: async (id) => {
    const response = await api.get(`/site-projects/${id}`);
    return response.data;
  },
  getDropdownList: async () => {
    const response = await api.get('/site-projects/dropdown/list');
    return response.data;
  },
  create: async (data) => {
    const response = await api.post('/site-projects', data);
    return response.data;
  },
  update: async (id, data) => {
    const response = await api.put(`/site-projects/${id}`, data);
    return response.data;
  },
  delete: async (id) => {
    const response = await api.delete(`/site-projects/${id}`);
    return response.data;
  },
  getStats: async () => {
    const response = await api.get('/site-projects/stats/overview');
    return response.data;
  },
  updateWorkItems: async (id, work_items) => {
    const response = await api.put(`/site-projects/${id}/work-items`, { work_items });
    return response.data;
  },
  updateMilestones: async (id, milestones) => {
    const response = await api.put(`/site-projects/${id}/milestones`, { milestones });
    return response.data;
  },
  addPhoto: async (id, data) => {
    const response = await api.post(`/site-projects/${id}/photos`, data);
    return response.data;
  },
  deletePhoto: async (projectId, photoId) => {
    const response = await api.delete(`/site-projects/${projectId}/photos/${photoId}`);
    return response.data;
  },
  addExpense: async (id, data) => {
    const response = await api.post(`/site-projects/${id}/expenses`, data);
    return response.data;
  },
  deleteExpense: async (projectId, expenseId) => {
    const response = await api.delete(`/site-projects/${projectId}/expenses/${expenseId}`);
    return response.data;
  },
  // --- Phase 2: Work Details + Material at Site ---
  listWorkDetails: async (id) => {
    const response = await api.get(`/site-projects/${id}/work-details`);
    return response.data;
  },
  addWorkDetail: async (id, data) => {
    const response = await api.post(`/site-projects/${id}/work-details`, data);
    return response.data;
  },
  updateWorkDetail: async (id, wdId, data) => {
    const response = await api.put(`/site-projects/${id}/work-details/${wdId}`, data);
    return response.data;
  },
  deleteWorkDetail: async (id, wdId) => {
    const response = await api.delete(`/site-projects/${id}/work-details/${wdId}`);
    return response.data;
  },
  getMaterialAtSite: async (id) => {
    const response = await api.get(`/site-projects/${id}/material-at-site`);
    return response.data;
  },
  // --- Phase 3: Service Invoice / Labour Invoice / Transportation ---
  listServiceInvoices: async (id) => {
    const response = await api.get(`/site-projects/${id}/service-invoices`);
    return response.data;
  },
  createServiceInvoice: async (id, data) => {
    const response = await api.post(`/site-projects/${id}/service-invoices`, data);
    return response.data;
  },
  getServiceInvoice: async (id, invId) => {
    const response = await api.get(`/site-projects/${id}/service-invoices/${invId}`);
    return response.data;
  },
  deleteServiceInvoice: async (id, invId) => {
    const response = await api.delete(`/site-projects/${id}/service-invoices/${invId}`);
    return response.data;
  },
  listLabourInvoices: async (id) => {
    const response = await api.get(`/site-projects/${id}/labour-invoices`);
    return response.data;
  },
  createLabourInvoice: async (id, data) => {
    const response = await api.post(`/site-projects/${id}/labour-invoices`, data);
    return response.data;
  },
  deleteLabourInvoice: async (id, invId) => {
    const response = await api.delete(`/site-projects/${id}/labour-invoices/${invId}`);
    return response.data;
  },
  // Labour advances
  listLabourAdvances: async (id) => {
    const response = await api.get(`/site-projects/${id}/labour-advances`);
    return response.data;
  },
  createLabourAdvance: async (id, data) => {
    const response = await api.post(`/site-projects/${id}/labour-advances`, data);
    return response.data;
  },
  deleteLabourAdvance: async (id, advId) => {
    const response = await api.delete(`/site-projects/${id}/labour-advances/${advId}`);
    return response.data;
  },
  labourAdvanceBalance: async (id, contractor) => {
    const response = await api.get(`/site-projects/${id}/labour-advances/balance`, { params: { contractor } });
    return response.data;
  },
  contractorSuggestions: async (id) => {
    const response = await api.get(`/site-projects/${id}/contractor-suggestions`);
    return response.data;
  },
  pendingWorkSummary: async (id, params) => {
    const response = await api.get(`/site-projects/${id}/pending-work-summary`, { params: params || {} });
    return response.data;
  },
  getTransportation: async (id) => {
    const response = await api.get(`/site-projects/${id}/transportation`);
    return response.data;
  },
  getAnalytics: async (id) => {
    const response = await api.get(`/site-projects/${id}/analytics`);
    return response.data;
  },
};


export const masterChangeAPI = {
  create: async (data) => {
    const response = await api.post('/master-changes', data);
    return response.data;
  },
  getAll: async (params = {}) => {
    const response = await api.get('/master-changes', { params });
    return unwrapItems(response.data);
  },
  getStats: async () => {
    const response = await api.get('/master-changes/stats');
    return response.data;
  },
  approve: async (id, reason = '') => {
    const response = await api.put(`/master-changes/${id}/approve`, { reason });
    return response.data;
  },
  reject: async (id, reason = '') => {
    const response = await api.put(`/master-changes/${id}/reject`, { reason });
    return response.data;
  },
};


export const salesReturnAPI = {
  list: async (params = {}) => {
    const response = await api.get('/sales-returns', { params });
    return response.data;
  },
  get: async (id) => {
    const response = await api.get(`/sales-returns/${id}`);
    return response.data;
  },
  create: async (data) => {
    const response = await api.post('/sales-returns', data);
    return response.data;
  },
  verify: async (id, data) => {
    const response = await api.put(`/sales-returns/${id}/verify`, data);
    return response.data;
  },
  approve: async (id, data) => {
    const response = await api.put(`/sales-returns/${id}/approve`, data);
    return response.data;
  },
  getOCItems: async (ocId) => {
    const response = await api.get(`/sales-returns/oc-items/${ocId}`);
    return response.data;
  },
  uploadDocument: async (id, file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post(`/sales-returns/${id}/documents`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },
  removeDocument: async (id, docIndex) => {
    const response = await api.delete(`/sales-returns/${id}/documents/${docIndex}`);
    return response.data;
  },
  getReasons: async () => {
    const response = await api.get('/sales-returns/reasons');
    return response.data;
  },
};

export const rolePermissionsAPI = {
  get: async () => {
    const response = await api.get('/role-permissions');
    return response.data;
  },
  update: async (permissions) => {
    const response = await api.put('/role-permissions', { permissions });
    return response.data;
  },
  reset: async () => {
    const response = await api.post('/role-permissions/reset');
    return response.data;
  },
};


export const adminAPI = {
  cleanAllData: async () => {
    const response = await api.delete('/admin/clean-all-data');
    return response.data;
  },
  purgeTransactions: async () => {
    const response = await api.delete('/admin/purge-transactions');
    return response.data;
  },
};

export const salespersonAPI = {
  getAll: async () => {
    const response = await api.get('/salespersons');
    return response.data;
  },
  create: async (data) => {
    const response = await api.post('/salespersons', data);
    return response.data;
  },
  update: async (id, data) => {
    const response = await api.put(`/salespersons/${id}`, data);
    return response.data;
  },
  delete: async (id) => {
    const response = await api.delete(`/salespersons/${id}`);
    return response.data;
  },
};

export const companySettingsAPI = {
  getBankDetails: async () => {
    const response = await api.get('/company-settings/bank-details');
    return response.data;
  },
  updateBankDetails: async (data) => {
    const response = await api.put('/company-settings/bank-details', data);
    return response.data;
  },
};



// ===== Tally Analytics Engine (Business Insight dashboard) =====
export const analyticsAPI = {
  // NOTE: Kept as a thin wrapper for now in case any future page wants to read
  // the cached Tally analytics endpoints. The standalone "Business Insight" page
  // was removed in Feb 2026 — see CHANGELOG.
  overview: async () => (await api.get('/analytics/overview')).data,
};

// ===== Monthly Review Center (Board Pack) =====
export const boardPackAPI = {
  sections: async () => (await api.get('/board-packs/sections')).data,
  preview: async (data) => (await api.post('/board-packs/preview', data)).data,
  getOverrides: async (params) => (await api.get('/board-packs/uploads', { params })).data,
  uploadOverride: async (params, file, previousFile) => {
    const fd = new FormData();
    Object.entries(params).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== '') fd.append(k, v); });
    fd.append('file', file);
    if (previousFile) fd.append('previous_file', previousFile);
    return (await api.post('/board-packs/uploads', fd, { headers: { 'Content-Type': 'multipart/form-data' } })).data;
  },
  deleteOverride: async (id) => (await api.delete(`/board-packs/uploads/${id}`)).data,
  downloadTemplate: async (section) => {
    const response = await api.get(`/board-packs/templates/${section}`, { responseType: 'blob' });
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const a = document.createElement('a');
    a.href = url;
    a.download = `board_pack_${section}_template.xlsx`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  },
  downloadPptx: async (data) => {
    const response = await api.post('/board-packs/pptx', data, { responseType: 'blob' });
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const a = document.createElement('a');
    const cd = response.headers['content-disposition'] || '';
    const m = cd.match(/filename="?([^";]+)"?/);
    a.href = url;
    a.download = m ? m[1] : 'BoardPack.pptx';
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  },
  downloadRawData: async (data) => {
    const response = await api.post('/board-packs/raw-data', data, { responseType: 'blob' });
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const a = document.createElement('a');
    const cd = response.headers['content-disposition'] || '';
    const m = cd.match(/filename="?([^";]+)"?/);
    a.href = url;
    a.download = m ? m[1] : 'BoardPack_RawData.xlsx';
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  },
};
